import { useMemo } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useResultsBehindProfile } from './useResultsBehindProfile';
import { useAnalysisResultsQuery } from '../api/useAnalysisResultsQuery';
import { useAnalysisVersionQuery } from '../api/useAnalysisVersionQuery';
import { selectLoadedProfileId, selectLoadedVersionId } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { ResultsStaleness, UseAnalysisDefinitionTabResult } from '../types';

export const useAnalysisDefinitionTab = (): UseAnalysisDefinitionTabResult => {
  const { projectId, analysisProfileId, analysisVersionId } = useAnalysisRouteParams();

  const versionQuery = useAnalysisVersionQuery({
    projectId,
    analysisProfileId,
    analysisVersionId,
  });
  const loadedVersionId = useAnalysisDefinitionStore(selectLoadedVersionId);
  const loadedProfileId = useAnalysisDefinitionStore(selectLoadedProfileId);

  const resultsData = useAnalysisResultsQuery({ projectId, analysisProfileId }).data?.data;
  // The cached results still carry the previous run's stale flag until the
  // post-run refetch lands, so the banner is withheld rather than shown stale.
  const isResultsBehindProfile = useResultsBehindProfile();
  const resultsStaleness = useMemo<ResultsStaleness | null>(
    () =>
      resultsData?.stale && !isResultsBehindProfile
        ? { reasons: Array.from(new Set(resultsData.staleReasons)) }
        : null,
    [resultsData, isResultsBehindProfile]
  );

  // "Loading" = there is no definition to show yet: route params not ready, the
  // first fetch in flight, or the store not yet seeded for this analysis. Once the
  // store holds a version of this analysis, a background refetch or a move to a
  // newer version (an RA edit saves one and the route follows it) keeps the current
  // content on screen and swaps it in place when the new version hydrates.
  const hasLoadedThisAnalysis = loadedVersionId !== null && loadedProfileId === analysisProfileId;
  const isLoading =
    versionQuery.isLoading ||
    !analysisVersionId ||
    (loadedVersionId !== analysisVersionId && !hasLoadedThisAnalysis);

  return { isLoading, resultsStaleness };
};
